import { yupResolver } from "@hookform/resolvers/yup"
import { useForm } from "react-hook-form"
import * as yup from "yup"
import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import ModalHeader from "../../../../Components/ModalHeader/ModalHeader"
import { ModalFooter } from "../../../../Constants/StyledComponents"
import MultivalueTextField from "../../../../Components/TextField/MultivalueTextField"
import ModernMUITable from "../../../../Components/ModernMUITable"
import { useTableContext } from "../../../../Providers/TableProvider"
import { useEndpointProvider } from "../../../../Providers/EndpointProvider"
import { useModalProvider } from "../../../../Providers/ModalProvider"
import GridContainer from "../../../../Components/TextField/GridContainer"
import TableWrapper from "../../../../Components/TextField/TableWrapper"
import ModalScrollbar from "../../../../Components/ModalScrollbar/ModalScrollbar"
import { MobergButton, MobergButtonShape, MobergButtonVariant } from "../../../../Components/MobergButton/MobergButton"
import { MobergFontSize } from "../../../../Components/MobergFont/MobergFont"
import { MobergTheme } from "../../../../Components/MobergThemes/MobergColors"
import { useBackendLinksProvider } from "../../../../Providers/BackendLinksProvider"
import { Deployment, useEnvironmentVariablesProvider } from "../../../../Providers/EnvironmentVariablesProvider"

const projectColumns = [
	{ field: "identifier", flex: 0.4, headerName: "Project ID", visible: true },
	{ field: "name", flex: 0.4, headerName: "Name", visible: true },
	{ field: "bucket_name", flex: 0.4, headerName: "Repository", visible: true },
	{ field: "user_count", flex: 0.4, headerName: "No. users", valueGetter: (value, row) => row.user_count || 0, visible: true },
	{ field: "patient_count", flex: 0.4, headerName: "No. patients", valueGetter: (value, row) => row.patient_count || 0, visible: true },
]

const siteColumns = [
	{ field: "identifier", flex: 0.4, headerName: "Site ID", visible: true },
	{ field: "name", flex: 1, headerName: "Site name", visible: true },
	{ field: "description", flex: 1, headerName: "Description", visible: true },
	{ field: "location", flex: 1, headerName: "Location", visible: true },
	{ field: "user_count", flex: 0.4, headerName: "No. users", visible: true, valueGetter: (value, row) => row.user_count || 0 },
	{ field: "patient_count", flex: 0.4, headerName: "No. patients", visible: true, valueGetter: (value, row) => row.patient_count || 0 },
]

const roleColumns = [
	{ field: "name", flex: 1, headerName: "Role Name", visible: true },
	{ field: "description", flex: 1, headerName: "Description", visible: true },
]

// Simple table components for the modal
function ProjectTable({ columns, queryProps, selectedRows, setSelectedRows }) {
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
	
	return (
		<ModernMUITable
			columns={columns}
			queryKey={queryProps.queryKey}
			endpoint={queryProps.endpoint}
			body={{}}
			optionalParams={{}}
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			paginationMode="server"
			enableRowSelection={true}
			onSelectionChange={setSelectedRows}
			sortingMode="server"
			height={300}
			showToolbar={false}
			supportsPageSize={true}
		/>
	)
}

function SiteTable({ columns, queryProps, selectedRows, setSelectedRows }) {
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
	
	return (
		<ModernMUITable
			columns={columns}
			queryKey={queryProps.queryKey}
			endpoint={queryProps.endpoint}
			body={{}}
			optionalParams={{}}
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			paginationMode="server"
			enableRowSelection={true}
			onSelectionChange={setSelectedRows}
			sortingMode="server"
			height={300}
			showToolbar={false}
			supportsPageSize={true}
		/>
	)
}

function RoleTable({ columns, queryProps, selectedRows, setSelectedRows }) {
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })
	
	return (
		<ModernMUITable
			columns={columns}
			queryKey={queryProps.queryKey}
			endpoint={queryProps.endpoint}
			body={{}}
			optionalParams={{}}
			paginationModel={paginationModel}
			onPaginationModelChange={setPaginationModel}
			paginationMode="server"
			enableRowSelection={true}
			onSelectionChange={setSelectedRows}
			sortingMode="server"
			height={300}
			showToolbar={false}
			supportsPageSize={true}
		/>
	)
}

export default function InviteUserModal() {
	const { setToastProps } = useTableContext()
	const queryClient = useQueryClient()
	const endpointProvider = useEndpointProvider()
	const { close } = useModalProvider()
	const { LINKS } = useBackendLinksProvider()
	const { deployment } = useEnvironmentVariablesProvider()
	const isClinical = deployment === Deployment.CLINICAL

	// Strict email regex
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

	const schema = yup
		.object({
			emails: yup
			.array()
			.of(
				yup
				.string()
				.email('Invalid email format')
				.matches(emailRegex, 'Invalid email format')
				.required('Email is required'),
			)
			.min(1, 'At least one email is required')
			.required('Emails are required'),
		})
		.required();

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: { emails: [] },
		mode: "onChange",
	})

	const projectQueryProps = {
		queryKey: "projects",
		endpoint: LINKS.ADMIN.PROJECTS.GET_PROJECTS,
	}

	const siteQueryProps = {
		queryKey: "sites",
		endpoint: LINKS.ADMIN.SITES.GET_SITES,
	}

	const roleQueryProps = {
		queryKey: "roles",
		endpoint: LINKS.ADMIN.USER_MANAGEMENT.USERS.GET_ROLES,
	}

	const [selectedProjectRows, setSelectedProjectRows] = useState([])
	const [selectedSiteRows, setSelectedSiteRows] = useState([])
	const [selectedRoleRows, setSelectedRoleRows] = useState([])

	const inviteUsers = async body => endpointProvider.post(LINKS.ADMIN.USER_MANAGEMENT.USERS.INVITE_USERS, body)
	const inviteUserMutation = useMutation({
		mutationFn: body => inviteUsers(body),
		onError: error => {
			setToastProps({ isOpen: true, severity: "error", message: error })
		},
		onSuccess: () => {
			setToastProps({ isOpen: true, severity: "success", message: "User(s) invited successfully." })
		},
		onSettled: () => {
			queryClient.invalidateQueries("users")
		},
	})

	const [errorMessage, setErrorMessage] = useState('')

	const onSubmit = data => {
		// Only validate roles for clinical, validate all three for cloud
		if (isClinical) {
			if (selectedRoleRows.length === 0) {
				setErrorMessage("Please select at least one role")
				return
			}
		} else {
			if (selectedProjectRows.length === 0 || selectedSiteRows.length === 0 || selectedRoleRows.length === 0) {
				setErrorMessage("Please select at least one project, site and role")
				return
			}
		}
		inviteUserMutation.mutate({
			...data,
			project_ids: isClinical ? [2] : selectedProjectRows,
			site_ids: isClinical ? [2] : selectedSiteRows,
			role_ids: selectedRoleRows,
		})
		setErrorMessage('')
		close()
	}

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			const error = errors[Object.keys(errors)[0]]
			if (Array.isArray(error)) {
				error.forEach(e => {
					if (e) {
						setErrorMessage(e?.message)
					}
				})
			} else {
				setErrorMessage(error?.message)
			}
		}
	}, [errors])

	return (
		<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
			<ModalHeader headerText="Invite user" />
			<ModalScrollbar>
				<div style={{ maxWidth: "800px" }}>
					<h2>User Details</h2>
					<GridContainer>
						<MultivalueTextField
							inputId="invite-user-emails-id"
							label="Emails"
							tooltip="When a user is invited, they will be sent an email to this address with an invitation to join the workspace. They will also receive a separate email to create an account if they do not have one associated with this email."
							placeholder="admin@moberganalytics.com"
							autoFocus={true}
							control={control}
							name="emails"
							numberColumn={12}
						/>

						{!isClinical && (
							<>
								<TableWrapper inputId="invite-user-projects-id" label="Projects" tooltip="The projects that the user will be invited to.">
									<ProjectTable 
										columns={projectColumns} 
										queryProps={projectQueryProps} 
										selectedRows={selectedProjectRows}
										setSelectedRows={setSelectedProjectRows}
									/>
								</TableWrapper>

								<TableWrapper inputId="invite-user-sites-id" label="Sites" tooltip="The sites that the user will be invited to.">
									<SiteTable 
										columns={siteColumns} 
										queryProps={siteQueryProps}
										selectedRows={selectedSiteRows}
										setSelectedRows={setSelectedSiteRows}
									/>
								</TableWrapper>
							</>
						)}

						<TableWrapper inputId="invite-user-roles-id" label="Roles" tooltip="The roles that the user will be invited to.">
							<RoleTable 
								columns={roleColumns} 
								queryProps={roleQueryProps}
								selectedRows={selectedRoleRows}
								setSelectedRows={setSelectedRoleRows}
							/>
						</TableWrapper>
					</GridContainer>
				</div>
			</ModalScrollbar>
			<ModalFooter style={{ justifyContent: 'space-between', alignItems: 'center' }}>
				<h1 style={{ color: '#E54E58' }}>{errorMessage}</h1>
				<MobergButton
					theme={MobergTheme.BLUE}
					variant={MobergButtonVariant.FILLED}
					onClick={handleSubmit(onSubmit)}
					shape={MobergButtonShape.WIDE}
					fontSize={MobergFontSize.LARGE}
				>
					Invite
				</MobergButton>
			</ModalFooter>
		</div>
	)
}